import { useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { getOutletKeywordGroups } from "../graphql/queries/getOutletKeywordGroups";

/**
 * Fetches OUTLET keyword groups (Stores) and derives the distinct `category`
 * values (Store Groups) client-side.
 *
 * Called with no args it fetches the full unfiltered OUTLET list — this is what
 * seeds the Store Group (category) picker, so category derivation must always run
 * against the unfiltered set. Pass `{ category, metadataFilters }` to apply the
 * admin-bff `KeywordGroupFilter` server-side (ADO 26708962): `category` narrows to a
 * Store Group and `metadataFilters: [{ path, value }]` applies `metadata.<path> =
 * value` (equality, AND-combined) — used to narrow the store table in step 2.
 *
 * @param {{ category?: string, metadataFilters?: Array<{path: string, value: any}>, allowedCategories?: string[], skip?: boolean }} [options]
 * @returns {{
 *   stores: Array,                               // OUTLET groups (filtered when options given), name-sorted
 *   categories: Array<{id: string, displayText: string}>,  // distinct Store Groups
 *   getStoresForCategory: (category: string) => Array,
 *   loading: boolean,
 *   error: object,
 *   refetch: Function,
 * }}
 */
export const useOutletKeywordGroups = ({
  category,
  metadataFilters,
  allowedCategories,
  skip = false,
} = {}) => {
  const filters = useMemo(() => {
    const next = {};
    if (category) next.category = category;
    if (metadataFilters?.length) next.metadataFilters = metadataFilters;
    return Object.keys(next).length ? next : undefined;
  }, [category, metadataFilters]);

  const { data, loading, error, refetch } = useQuery(getOutletKeywordGroups, {
    variables: { filters },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    skip,
  });

  const stores = useMemo(() => {
    if (!data?.listKeywordGroups) return [];
    return [...data.listKeywordGroups]
      .map((group) => ({
        id: group.id,
        name: group.name,
        slug: group.slug,
        type: group.type,
        imageUrl: group.imageUrl,
        category: group.category,
        keywords: group.keywords || [],
        metadata: group.metadata || {},
      }))
      .filter(
        (store) =>
          !Array.isArray(allowedCategories) ||
          allowedCategories.includes(store.category)
      )
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [data, allowedCategories]);

  const categories = useMemo(() => {
    return [...new Set(stores.map((store) => store.category))]
      .filter((cat) => !!cat)
      .sort((a, b) => a.localeCompare(b))
      .map((cat) => ({ id: cat, displayText: cat }));
  }, [stores]);

  const getStoresForCategory = useCallback(
    (cat) => stores.filter((store) => store.category === cat),
    [stores]
  );

  return {
    stores,
    categories,
    getStoresForCategory,
    loading,
    error,
    refetch,
  };
};
